/**
 * Product Block Add to Cart Form
 * Handles quick add to cart functionality for product blocks
 * Works with a single custom-select dropdown for all variants
 */

if (!customElements.get('product-block-atc-form')) {
  class ProductBlockAtcForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.customSelect = this.querySelector('custom-select');
      this.variantInput = this.querySelector('input[name="id"]');
      this.submitButton = this.querySelector('button[type="submit"]');
      this.buttonText = this.querySelector('.product-block-atc-form__button-text');
      
      if (!this.form || !this.submitButton) return;
      
      this.bindEvents();
    }

    bindEvents() {
      // Listen for custom-select changes
      if (this.customSelect) {
        this.customSelect.addEventListener('change', this.handleVariantChange.bind(this));
      }
      
      // Handle form submission
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Handle variant selection change from custom-select
     */
    handleVariantChange(evt) {
      const selectedOption = this.customSelect.querySelector('.custom-select__option[aria-selected="true"]');
      
      if (selectedOption) {
        const variantId = selectedOption.dataset.value;
        const variantName = selectedOption.querySelector('span').textContent;
        const isSoldOut = variantName.includes('Sold Out') || variantName.includes('sold out');
        
        // Update hidden input
        this.variantInput.value = variantId;
        
        // Enable/disable button based on availability
        this.submitButton.disabled = isSoldOut;
        
        // Update button text based on availability
        if (isSoldOut) {
          this.buttonText.textContent = theme.strings.noStock || 'Sold Out';
        } else {
          this.buttonText.textContent = this.submitButton.dataset.addToCartText || 'Add to Cart';
        }
      } else {
        // No variant selected
        this.variantInput.value = '';
        this.submitButton.disabled = true;
        this.buttonText.textContent = this.submitButton.dataset.addToCartText || 'Add to Cart';
      }
    }

    /**
     * Handle form submission
     * Submit via AJAX and open cart drawer
     */
    async handleSubmit(evt) {
      evt.preventDefault();
      
      if (this.submitButton.disabled) return;
      
      // Set loading state
      this.setLoadingState(true);
      
      const formData = new FormData(this.form);
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          let error = typeof data.description === 'string' ? data.description : data.message;
          if (data.errors && typeof data.errors === 'object') {
            error = Object.entries(data.errors).map((item) => item[1].join(', ')).join(', ');
          }
          throw new Error(error);
        }
        
        // Set success state briefly
        this.setSuccessState();
        
        // Dispatch cart change event
        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        
        // Update cart sections if available
        const sectionsToUpdate = document.querySelectorAll('[data-section-type="cart-drawer"], [data-section-type="cart-icon"]');
        if (data.sections && sectionsToUpdate.length > 0) {
          sectionsToUpdate.forEach((el) => {
            if (el.updateFromCartChange && data.sections[el.dataset.sectionId]) {
              el.updateFromCartChange(data.sections[el.dataset.sectionId]);
            }
          });
        }
        
        // Open cart drawer
        setTimeout(() => {
          const cartDrawer = document.querySelector('.js-cart-drawer, cart-drawer');
          if (cartDrawer && cartDrawer.open) {
            cartDrawer.open();
          }
          
          this.setLoadingState(false);
          
          // Reset form if multi-variant
          if (this.customSelect && this.customSelect.reset) {
            this.customSelect.reset();
            this.variantInput.value = '';
            this.submitButton.disabled = true;
            this.buttonText.textContent = this.submitButton.dataset.addToCartText || 'Add to Cart';
          }
        }, 300);
        
      } catch (error) {
        console.error('Add to cart error:', error);
        this.showError(error.message);
        this.setLoadingState(false);
      }
    }

    /**
     * Set loading state on button
     */
    setLoadingState(isLoading) {
      this.submitButton.classList.toggle('is-loading', isLoading);
      this.submitButton.disabled = isLoading;
      
      if (isLoading) {
        this.originalButtonText = this.buttonText.textContent;
        this.buttonText.textContent = theme.strings.adding || 'Adding...';
      } else if (this.originalButtonText) {
        this.buttonText.textContent = this.originalButtonText;
      }
    }

    /**
     * Set success state on button briefly
     */
    setSuccessState() {
      this.submitButton.classList.add('is-success');
      const originalText = this.buttonText.textContent;
      this.buttonText.textContent = theme.strings.added || 'Added!';
      
      setTimeout(() => {
        this.submitButton.classList.remove('is-success');
      }, 2000);
    }

    /**
     * Show error message
     */
    showError(message) {
      // Simple console error for now
      // Could be enhanced with UI error display
      console.error('Cart error:', message);
      
      // Update button text briefly
      const originalText = this.buttonText.textContent;
      this.buttonText.textContent = 'Error';
      this.submitButton.classList.add('is-error');
      
      setTimeout(() => {
        this.buttonText.textContent = originalText;
        this.submitButton.classList.remove('is-error');
      }, 2000);
    }
  }

  customElements.define('product-block-atc-form', ProductBlockAtcForm);
}